import { useEffect, useState, useRef } from "react";
import { AdminShell } from "@/components/Layout";
import { api, API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Plus, X, Trash, FileText, PaperPlaneTilt, Signature, Warning, Camera, Image as ImageIcon, Sparkle, ShieldCheck, ShieldWarning, Scales } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const STATUS_BADGE = {
  draft: "bg-slate-300 text-slate-900",
  sent: "bg-amber-500 text-white",
  signed: "bg-emerald-500 text-white",
  disputed: "bg-red-600 text-white",
};

const DED_CATS = ["damage", "cleaning", "unpaid_rent", "trash", "other"];

export default function DepositReconciliations() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/deposits");
      setRows(r.data || []);
    } catch { toast.error("Failed to load deposits"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="deposits-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <Receipt weight="fill" className="text-emerald-500"/> Deposit Reconciliation
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Move-in/move-out condition reports with itemized deposit deductions. Tenants review & e-sign from a magic link — no arguments, no small-claims-court surprises.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-deposit-btn"
            className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> New Reconciliation
          </button>
        </div>

        <div className="rugged-card p-4">
          {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
          : rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No deposit reconciliations yet. Create one when a tenant moves out.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map(d => (
                <div key={d.id} className="p-3 border-2 border-slate-200 hover:border-slate-400 transition"
                  data-testid={`deposit-row-${d.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-black text-sm">{d.tenant_name}</div>
                        <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[d.status]}`}>
                          {d.status}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5">
                        {d.property_address} {d.unit && `· ${d.unit}`} · Moved out {d.move_out_date || "—"}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        Held: <b>{money(d.security_deposit_cents)}</b> · Deductions: <b>{money(d.deductions_total_cents)}</b> ·
                        {d.refund_cents > 0
                          ? <span className="text-emerald-700 font-black"> Refund {money(d.refund_cents)}</span>
                          : <span className="text-red-700 font-black"> Owed {money(d.owed_by_tenant_cents)}</span>}
                      </div>
                      {d.status === "signed" && (
                        <div className="text-[10px] text-emerald-700 mt-0.5">
                          ✓ Signed by {d.signed_by_name} on {d.signed_at?.slice(0, 10)}
                        </div>
                      )}
                      {d.status === "disputed" && (
                        <div className="mt-1 p-1.5 bg-red-50 border-l-4 border-red-500 text-[10px]">
                          <b>Tenant disputes:</b> {d.dispute_reason?.slice(0, 200)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 shrink-0">
                      <button onClick={() => setEditing(d)} data-testid={`edit-deposit-${d.id}`}
                        className="h-9 px-3 bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a]">
                        Edit
                      </button>
                      <DepositPdfLink id={d.id}/>
                      <DepositSendBtn deposit={d} onSent={load}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showNew && (
          <DepositModal onClose={() => setShowNew(false)}
            onSaved={() => { setShowNew(false); load(); }}/>
        )}
        {editing && (
          <DepositModal existing={editing} onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function DepositPdfLink({ id }) {
  const download = async () => {
    try {
      const res = await api.get(`/deposits/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `deposit-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };
  return (
    <button onClick={download} data-testid={`pdf-deposit-${id}`}
      className="h-9 px-3 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
      <FileText size={12}/> PDF
    </button>
  );
}

function DepositSendBtn({ deposit, onSent }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!deposit.tenant_email && !window.prompt("No tenant email on file. Enter one to send:")) return;
    setBusy(true);
    try {
      const r = await api.post(`/deposits/${deposit.id}/send`, {});
      toast.success(r.data?.email_sent ? "Sent to tenant" : "Link ready (email blocked by killswitch)");
      onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
    finally { setBusy(false); }
  };
  if (deposit.status === "signed") return null;
  return (
    <button onClick={send} disabled={busy} data-testid={`send-deposit-${deposit.id}`}
      className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1 disabled:opacity-50">
      <PaperPlaneTilt size={12}/> {deposit.status === "sent" ? "Resend" : "Send"}
    </button>
  );
}

function DepositModal({ existing, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    tenant_name: "", tenant_email: "", property_address: "", unit: "",
    move_in_date: "", move_out_date: "", security_deposit_cents: 0,
    move_in_items: [], move_out_items: [], deductions: [],
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("basics");

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...form,
        security_deposit_cents: Math.round((parseFloat(form.security_deposit_cents) || 0) * (existing ? 1 : 100)),
        deductions: (form.deductions || []).map(x => ({
          ...x,
          amount_cents: Math.round((parseFloat(x.amount_cents) || 0) * (existing && x.__saved ? 1 : 100)),
        })),
      };
      // when editing, values are already in cents
      if (existing) {
        body.security_deposit_cents = parseInt(form.security_deposit_cents) || 0;
        body.deductions = form.deductions;
        await api.patch(`/deposits/${existing.id}`, body);
      } else {
        await api.post("/deposits", body);
      }
      toast.success(existing ? "Updated" : "Created — default checklist seeded");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const addDeduction = () => setForm({...form, deductions: [
    ...(form.deductions || []),
    { description: "", category: "damage", amount_cents: 0, notes: "" }
  ]});
  const patchDed = (i, k, v) => {
    const next = [...form.deductions]; next[i] = {...next[i], [k]: v};
    setForm({...form, deductions: next});
  };
  const delDed = (i) => {
    const next = [...form.deductions]; next.splice(i, 1);
    setForm({...form, deductions: next});
  };
  const patchItem = (list, i, k, v) => {
    const next = [...(form[list] || [])]; next[i] = {...next[i], [k]: v};
    setForm({...form, [list]: next});
  };

  const dedTotal = (form.deductions || []).reduce((s, x) => s + (parseFloat(x.amount_cents) || 0) * (existing ? 1 : 100), 0);
  const secCents = parseFloat(form.security_deposit_cents) * (existing ? 1 : 100) || 0;
  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="deposit-modal">
      <div className="bg-white w-full max-w-3xl border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] max-h-[92vh] overflow-y-auto">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
              {existing ? "Edit" : "New"} Reconciliation
            </div>
            <div className="font-black uppercase tracking-widest">
              {form.tenant_name || "New Tenant"}
            </div>
          </div>
          <button onClick={onClose} data-testid="deposit-modal-close" className="p-1 hover:bg-emerald-700"><X size={18}/></button>
        </div>
        <div className="flex gap-1 p-2 border-b-2 border-slate-200">
          {[
            {key: "basics", label: "Basics"},
            {key: "deductions", label: `Deductions (${(form.deductions || []).length})`},
            {key: "move_in", label: `Move-In (${(form.move_in_items || []).length})`},
            {key: "move_out", label: `Move-Out (${(form.move_out_items || []).length})`},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`h-9 px-3 font-black uppercase tracking-widest text-[10px] border-2 border-slate-900 ${
                tab === t.key ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-100"
              }`}>{t.label}</button>
          ))}
        </div>
        <div className="p-4 space-y-3">
          {tab === "basics" && (
            <div className="space-y-2">
              <input value={form.tenant_name} onChange={e => setForm({...form, tenant_name: e.target.value})} placeholder="Tenant name"
                data-testid="deposit-tenant-name"
                className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
              <input value={form.tenant_email} onChange={e => setForm({...form, tenant_email: e.target.value})} placeholder="Tenant email"
                className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
              <div className="grid grid-cols-3 gap-2">
                <input list="known-addresses" autoComplete="off" value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})}
                  placeholder="Address" data-testid="deposit-address"
                  className="col-span-2 h-10 px-3 border-2 border-slate-300 text-sm"/>
                <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="Unit"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
                  Move-in
                  <input value={form.move_in_date} onChange={e => setForm({...form, move_in_date: e.target.value})}
                    placeholder="YYYY-MM-DD"
                    className="h-10 px-3 border-2 border-slate-300 text-sm"/>
                </label>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
                  Move-out
                  <input value={form.move_out_date} onChange={e => setForm({...form, move_out_date: e.target.value})}
                    placeholder="YYYY-MM-DD"
                    className="h-10 px-3 border-2 border-slate-300 text-sm"/>
                </label>
              </div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
                Security deposit ({existing ? "cents" : "$"})
                <input type="number" value={form.security_deposit_cents}
                  onChange={e => setForm({...form, security_deposit_cents: e.target.value})}
                  data-testid="deposit-security"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              </label>
              <div className="p-3 bg-slate-50 border-2 border-slate-900">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Summary</div>
                <div className="grid grid-cols-3 gap-2 mt-1 text-sm">
                  <div><b>Held:</b> {money(secCents)}</div>
                  <div><b>Deductions:</b> {money(dedTotal)}</div>
                  <div className={secCents - dedTotal >= 0 ? "text-emerald-700" : "text-red-700"}>
                    <b>{secCents - dedTotal >= 0 ? "Refund:" : "Owed:"}</b> {money(Math.abs(secCents - dedTotal))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "deductions" && (
            <div className="space-y-2">
              {(form.deductions || []).map((x, i) => (
                <DeductionRow key={i} idx={i} ded={x} depositId={existing?.id}
                  onPatch={(k, v) => patchDed(i, k, v)}
                  onDelete={() => delDed(i)}
                  amountMode={existing ? "cents" : "$"}/>
              ))}
              <button onClick={addDeduction} data-testid="add-deduction"
                className="w-full h-10 border-2 border-dashed border-slate-400 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1">
                <Plus size={12}/> Add Deduction
              </button>
            </div>
          )}

          {(tab === "move_in" || tab === "move_out") && (
            <ChecklistEditor
              items={form[tab === "move_in" ? "move_in_items" : "move_out_items"] || []}
              onPatch={(i, k, v) => patchItem(tab === "move_in" ? "move_in_items" : "move_out_items", i, k, v)}
              depositId={existing?.id}
              phase={tab}/>
          )}

          <button onClick={save} disabled={busy || !form.tenant_name || !form.property_address}
            data-testid="deposit-save-btn"
            className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : (existing ? "Save Changes" : "Create Reconciliation")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeductionRow({ idx, ded, depositId, onPatch, onDelete, amountMode }) {
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(ded.ai_check || null);
  const [upBusy, setUpBusy] = useState(false);
  const miRef = useRef(null);
  const moRef = useRef(null);

  const miPaths = ded.move_in_photo_paths || [];
  const moPaths = ded.move_out_photo_paths || [];

  const upload = async (phase, file) => {
    if (!file) return;
    if (!depositId) { toast.error("Save the reconciliation first, then attach photos"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/deposits/${depositId}/deductions/${idx}/photo?phase=${phase}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPatch(`${phase}_photo_paths`, r.data.deduction[`${phase}_photo_paths`] || []);
      onPatch("ai_check", null);
      setAiResult(null);
      toast.success(`${phase === "move_in" ? "Move-in" : "Move-out"} photo added`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setUpBusy(false); }
  };

  const removePhoto = async (phase, path) => {
    if (!depositId) return;
    try {
      await api.delete(`/deposits/${depositId}/deductions/${idx}/photo?phase=${phase}&path=${encodeURIComponent(path)}`);
      onPatch(`${phase}_photo_paths`, (phase === "move_in" ? miPaths : moPaths).filter(p => p !== path));
      onPatch("ai_check", null);
      setAiResult(null);
    } catch (e) { toast.error("Remove failed"); }
  };

  const runAi = async () => {
    if (!depositId) { toast.error("Save the reconciliation first"); return; }
    if (!ded.description?.trim()) { toast.error("Add a description before running AI"); return; }
    setAiBusy(true);
    try {
      const r = await api.post(`/deposits/${depositId}/deductions/${idx}/ai-check`);
      setAiResult(r.data);
      onPatch("ai_check", r.data);
      toast.success(`AI review: ${r.data.defensibility}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI check failed")); }
    finally { setAiBusy(false); }
  };

  const acceptSuggestion = () => {
    if (!aiResult?.suggested_description) return;
    onPatch("description", aiResult.suggested_description);
    toast.success("Description updated");
  };

  const defBadge = {
    Excellent: "bg-emerald-600 text-white",
    Fair: "bg-amber-500 text-white",
    Weak: "bg-red-600 text-white",
  };
  const defIcon = { Excellent: ShieldCheck, Fair: Scales, Weak: ShieldWarning };
  const DefIcon = aiResult ? (defIcon[aiResult.defensibility] || Scales) : Scales;

  return (
    <div className="p-2 border-2 border-slate-200 space-y-2" data-testid={`ded-row-${idx}`}>
      <div className="grid grid-cols-6 gap-2">
        <select value={ded.category} onChange={e => onPatch("category", e.target.value)}
          className="col-span-2 h-9 px-2 border-2 border-slate-300 text-xs">
          {DED_CATS.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
        </select>
        <input value={ded.description} onChange={e => onPatch("description", e.target.value)}
          placeholder="Description" data-testid={`ded-desc-${idx}`}
          className="col-span-3 h-9 px-2 border-2 border-slate-300 text-xs"/>
        <div className="flex gap-1">
          <input type="number" value={ded.amount_cents} onChange={e => onPatch("amount_cents", e.target.value)}
            placeholder={amountMode} data-testid={`ded-amt-${idx}`}
            className="h-9 px-2 border-2 border-slate-300 text-xs w-full"/>
          <button onClick={onDelete} className="h-9 w-9 border-2 border-red-600 text-red-600 hover:bg-red-50 shrink-0">
            <Trash size={12}/>
          </button>
        </div>
      </div>
      <input value={ded.notes} onChange={e => onPatch("notes", e.target.value)} placeholder="Notes (optional)"
        className="w-full h-8 px-2 border-2 border-slate-200 text-xs"/>

      {/* Photos + AI */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <PhotoStrip label="MOVE-IN" phase="move_in" paths={miPaths}
          inputRef={miRef} onPick={f => upload("move_in", f)}
          onRemove={p => removePhoto("move_in", p)}
          disabled={!depositId || upBusy} idx={idx}/>
        <PhotoStrip label="MOVE-OUT" phase="move_out" paths={moPaths}
          inputRef={moRef} onPick={f => upload("move_out", f)}
          onRemove={p => removePhoto("move_out", p)}
          disabled={!depositId || upBusy} idx={idx}/>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="text-[10px] text-slate-500">
          {!depositId
            ? "Save reconciliation to enable photos + AI"
            : miPaths.length + moPaths.length === 0
              ? "Attach at least a move-out photo before running AI (move-in optional but strengthens the case)"
              : `${miPaths.length} move-in · ${moPaths.length} move-out photos attached`}
        </div>
        <button onClick={runAi} disabled={aiBusy || !depositId}
          data-testid={`ded-ai-check-${idx}`}
          className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1 disabled:opacity-50">
          <Sparkle weight="fill" size={12}/> {aiBusy ? "Assessing…" : (aiResult ? "Re-check" : "AI Damage Check")}
        </button>
      </div>

      {aiResult && (
        <div className="p-2 bg-slate-900 text-white border-2 border-slate-900 space-y-1"
          data-testid={`ded-ai-result-${idx}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={`h-6 px-2 leading-6 font-black uppercase tracking-widest text-[9px] ${defBadge[aiResult.defensibility] || "bg-slate-500"}`}>
                <DefIcon weight="fill" size={10} className="inline mr-1"/>{aiResult.defensibility}
              </span>
              {aiResult.is_normal_wear && (
                <span className="h-6 px-2 leading-6 font-black uppercase tracking-widest text-[9px] bg-amber-400 text-slate-900">
                  Normal wear
                </span>
              )}
              <span className="text-[9px] opacity-70">cost {aiResult.cost_bracket}</span>
            </div>
            <div className="text-[9px] opacity-70">{aiResult.photos_read || 0} photo{aiResult.photos_read === 1 ? "" : "s"} read</div>
          </div>
          {aiResult.suggested_description && (
            <div className="p-1.5 bg-white text-slate-900 border-2 border-fuchsia-400 text-xs">
              <div className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">Suggested wording (court-defensible)</div>
              <div className="mt-0.5">{aiResult.suggested_description}</div>
              <button onClick={acceptSuggestion}
                data-testid={`ded-ai-accept-${idx}`}
                className="mt-1 h-7 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[9px]">
                Use this wording
              </button>
            </div>
          )}
          {aiResult.reasoning && (
            <div className="text-[10px] opacity-90 leading-snug">
              <span className="font-black uppercase tracking-widest opacity-70">Why: </span>{aiResult.reasoning}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoStrip({ label, phase, paths, inputRef, onPick, onRemove, disabled, idx }) {
  return (
    <div className={`p-1.5 border-2 ${phase === "move_in" ? "border-slate-300 bg-slate-50" : "border-amber-400 bg-amber-50"}`}>
      <div className="flex items-center justify-between gap-1">
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-600">{label} ({paths.length}/8)</div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
          data-testid={`ded-${phase}-input-${idx}`}/>
        <button onClick={() => inputRef.current?.click()} disabled={disabled || paths.length >= 8}
          data-testid={`ded-${phase}-add-${idx}`}
          className="h-6 px-2 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 disabled:opacity-40">
          <Camera size={10}/> Add
        </button>
      </div>
      {paths.length > 0 && (
        <div className="flex gap-1 mt-1 flex-wrap">
          {paths.map((p, i) => (
            <div key={p} className="relative">
              <a href={fileUrl(p)} target="_blank" rel="noopener noreferrer">
                <img src={fileUrl(p)} alt="" className="h-10 w-10 object-cover border border-slate-400"
                  data-testid={`ded-${phase}-thumb-${idx}-${i}`}/>
              </a>
              <button onClick={() => onRemove(p)}
                data-testid={`ded-${phase}-del-${idx}-${i}`}
                className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-red-600 text-white rounded-full text-[9px] font-black leading-none">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChecklistEditor({ items, onPatch, depositId, phase }) {
  const fileInputRefs = useRef({});
  if (!items.length) {
    return <div className="text-sm text-slate-500 py-4 text-center">
      Checklist will be seeded with a default room-by-room template when you save.
    </div>;
  }
  const uploadPhoto = async (idx, file) => {
    if (!file) return;
    if (!depositId) {
      toast.error("Save the reconciliation first, then attach photos");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/deposits/${depositId}/photo?phase=${phase}&index=${idx}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo attached");
      onPatch(idx, "photo_url", r.data.photo_url);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
  };
  const removePhoto = async (idx) => {
    if (!depositId) return onPatch(idx, "photo_url", "");
    try {
      await api.delete(`/deposits/${depositId}/photo?phase=${phase}&index=${idx}`);
      onPatch(idx, "photo_url", "");
    } catch { toast.error("Remove failed"); }
  };
  const byRoom = {};
  items.forEach((it, idx) => { (byRoom[it.room || "Other"] = byRoom[it.room || "Other"] || []).push({...it, idx}); });
  return (
    <div className="space-y-3 max-h-[400px] overflow-y-auto">
      {!depositId && (
        <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px]">
          Photos become available after you save the reconciliation.
        </div>
      )}
      {Object.entries(byRoom).map(([room, entries]) => (
        <div key={room}>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{room}</div>
          <div className="space-y-1">
            {entries.map(e => (
              <div key={e.idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`chk-row-${e.idx}`}>
                <div className="col-span-3 text-xs">{e.item}</div>
                <select value={e.condition} onChange={ev => onPatch(e.idx, "condition", ev.target.value)}
                  className={`col-span-2 h-8 px-2 border-2 border-slate-300 text-xs ${
                    e.condition === "damaged" ? "bg-red-50" :
                    e.condition === "fair" ? "bg-amber-50" :
                    e.condition === "missing" ? "bg-red-100" : ""
                  }`}>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="damaged">Damaged</option>
                  <option value="missing">Missing</option>
                </select>
                <input value={e.notes} onChange={ev => onPatch(e.idx, "notes", ev.target.value)}
                  placeholder="Notes"
                  className="col-span-5 h-8 px-2 border-2 border-slate-200 text-xs"/>
                <div className="col-span-2 flex items-center gap-1 justify-end">
                  {e.photo_url ? (
                    <div className="relative">
                      <a href={fileUrl(e.photo_url)} target="_blank" rel="noopener noreferrer">
                        <img src={fileUrl(e.photo_url)} alt=""
                          className="h-8 w-8 object-cover border-2 border-emerald-500"
                          data-testid={`chk-photo-thumb-${e.idx}`}/>
                      </a>
                      <button onClick={() => removePhoto(e.idx)}
                        data-testid={`chk-photo-del-${e.idx}`}
                        className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-red-600 text-white rounded-full text-[9px] font-black leading-none">×</button>
                    </div>
                  ) : (
                    <>
                      <input ref={r => (fileInputRefs.current[e.idx] = r)}
                        type="file" accept="image/*"
                        onChange={ev => { const f = ev.target.files?.[0]; if (f) uploadPhoto(e.idx, f); ev.target.value = ""; }}
                        className="hidden"
                        data-testid={`chk-photo-input-${e.idx}`}/>
                      <button onClick={() => fileInputRefs.current[e.idx]?.click()}
                        disabled={!depositId}
                        data-testid={`chk-photo-btn-${e.idx}`}
                        title={depositId ? "Attach photo evidence" : "Save first to enable"}
                        className={`h-8 w-8 border-2 inline-flex items-center justify-center disabled:opacity-40 ${
                          e.condition === "damaged" || e.condition === "missing"
                            ? "border-red-500 bg-red-50 hover:bg-red-100 animate-pulse"
                            : "border-slate-300 hover:bg-slate-50"
                        }`}>
                        <ImageIcon size={14}/>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
